import * as React from 'react'
import { ProjectsController } from '../../controllers'
import Notification from '../../utilities/Notification'
import { withRouter, RouteComponentProps } from 'react-router'
import RouteHelper from '../../helpers/RouteHelper'
import ERoute from '../../ERoute';
import { AnyAction, Dispatch } from 'redux'
import { showBulkProjectStatusUpdateModal, showBulkProjectTagModal, showConfirmModal, showProjectModal } from '../../store/modals/actions'
import { connect } from 'react-redux'
import PageLoader from '../Page/PageLoader'
import CardEmptyInfo from '../Card/CardEmptyInfo'
import LocalStorage, { LocalStorageKey } from '../../LocalStorage'
import { Helmet } from 'react-helmet'
import { withTranslation, WithTranslation } from 'react-i18next'
import ResourceTable, { ResourceTableAction } from '../Resource/ResourceTable'
import { AppState } from '../../store'
import { CurrentUser, Project, ProjectColumnProperty, ProjectFilter, ProjectFilters, ProjectStatus, ResourceListFilterType } from '../../types'
import ButtonPanel from '../Button/ButtonPanel'
import ProjectHelper from '../../helpers/ProjectHelper'
import NavigationDetection from '../Effects/NavigationDetection'
import SearchHelper from '../../helpers/SearchHelper'
import getProjectTableRenderer, { DEFAULT_PROJECT_COLUMNS } from '../Resource/ProjectRenderer'

export interface IComponentProps {
  title: string
  status?: ProjectStatus
  onItemUpdated: () => void,
  onItemDeleted: () => void,
  contactId?: string
  refreshTimeline?: () => void
  primaryActionEnabled?: boolean
  urlRewriteEnabled?: boolean
}

interface IStateToProps {
  currentUser: CurrentUser
}

interface IDispatchToProps {
  showProjectModal: typeof showProjectModal
  showConfirmModal: typeof showConfirmModal
  showBulkProjectStatusUpdateModal: typeof showBulkProjectStatusUpdateModal
  showBulkProjectTagModal: typeof showBulkProjectTagModal
}

type IProps = IStateToProps & IComponentProps & IDispatchToProps & RouteComponentProps<any> & WithTranslation

interface IState {
  projects: Project[]
  page: number
  perPage: number
  totalPages: number
  didInitialLoad: boolean
  sortValue: string
  isFetching: boolean
  activeFilters: any
  search: string
  selectedProjectIds: string[]
}

class ProjectTable extends React.Component<IProps, IState> {
  constructor(props) {
    super(props)

    const { search, page, perPage, filters, } = SearchHelper.getSearch(this.props.location.search, ProjectFilters)

    this.state = {
      projects: [],
      page: page,
      perPage: perPage,
      totalPages: 1,
      didInitialLoad: false,
      isFetching: false,
      sortValue: LocalStorage.get(LocalStorageKey.PROJECT_SORT_VALUE, '-'),
      activeFilters: filters,
      search: search,
      selectedProjectIds: [],
    }

    this.onRouteChange = this.onRouteChange.bind(this)
    this.rehydratedFetch = this.rehydratedFetch.bind(this)
    this.onNewProjectClick = this.onNewProjectClick.bind(this)
    this.onEmptyInfoActionClick = this.onEmptyInfoActionClick.bind(this)
    this.onTableProjectActionClick = this.onTableProjectActionClick.bind(this)
    this.onTableSelectionChange = this.onTableSelectionChange.bind(this)
    this.onTableRowSelectionChange = this.onTableRowSelectionChange.bind(this)
    this.onTableProjectDeleteClick = this.onTableProjectDeleteClick.bind(this)
    this.onTableProjectClick = this.onTableProjectClick.bind(this)
    this.onProjectFiltersChange = this.onProjectFiltersChange.bind(this)
    this.onProjectSortValueChange = this.onProjectSortValueChange.bind(this)
    this.onProjectFormSubmit = this.onProjectFormSubmit.bind(this)
    this.onProjectUpdateFormSubmit = this.onProjectUpdateFormSubmit.bind(this)
    this.onProjectCreateEvent = this.onProjectCreateEvent.bind(this)
    this.onProjectSearchChange = this.onProjectSearchChange.bind(this)
    this.onProjectSearchSubmit = this.onProjectSearchSubmit.bind(this)
    this.onProjectClearFilters = this.onProjectClearFilters.bind(this)
    this.onPageChange = this.onPageChange.bind(this)
    this.onPerPageChange = this.onPerPageChange.bind(this)
    this.onActiveColumnsChange = this.onActiveColumnsChange.bind(this)
    this.onResourceBulkUpdateStatusActionClick = this.onResourceBulkUpdateStatusActionClick.bind(this)
    this.onResourceBulkDeleteActionClick = this.onResourceBulkDeleteActionClick.bind(this)
    this.onResourceBulkAssignTagsActionClick = this.onResourceBulkAssignTagsActionClick.bind(this)
    this.onResourceBulkRemoveTagsActionClick = this.onResourceBulkRemoveTagsActionClick.bind(this)
    this.onColumnsChange = this.onColumnsChange.bind(this)
  }

  getTitle() {
    const { status, t } = this.props

    switch (status) {
      case ProjectStatus.ACTIVE:
        return t('ProjectsTable::{{__appName}} | Active projects')
      case ProjectStatus.PROPOSAL:
        return t('ProjectsTable::{{__appName}} | Proposal projects')
      case ProjectStatus.COMPLETED:
        return t('ProjectsTable::{{__appName}} | Completed projects')
      case ProjectStatus.CANCELLED:
        return t('ProjectsTable::{{__appName}} | Cancelled projects')
      default:
        return t('ProjectsTable::{{__appName}} | Projects')
    }
  }

  componentDidMount() {
    const { page } = this.state

    document.addEventListener('project-create', this.onProjectCreateEvent)
  }

  componentWillUnmount() {
    document.removeEventListener('project-create', this.onProjectCreateEvent)
  }

  onRouteChange() {
    const { search, page, perPage, filters } = SearchHelper.getSearch(this.props.location.search, ProjectFilters)

    this.setState({ search: search, page: page, perPage: perPage, activeFilters: filters }, () => {
      this.fetchProjects(page)
    })
  }

  rehydratedFetch() {
    const { urlRewriteEnabled } = this.props
    const { page, perPage, activeFilters, search, sortValue } = this.state

    if (urlRewriteEnabled) {
      RouteHelper.replace(this.props.location.pathname as ERoute, {
        page: page,
        perPage: perPage,
        search: search,
        ...activeFilters,
        sortValue: sortValue
      })
    } else {
      this.fetchProjects(page)
    }
  }

  fetchProjects(page: number) {
    const { contactId, status } = this.props
    const { search, sortValue, activeFilters, perPage } = this.state

    this.setState({ isFetching: true })

    let params: any = {
      page: page,
      per_page: perPage,
      search: search,
      order: sortValue,
      ...activeFilters,
    }

    if (contactId) params.contact_id = contactId
    if (status) params.status = status

    ProjectsController
      .getProjects(params)
      .then(response => {
        const { current_page, total_pages, projects } = response

        this.setState({
          projects: [...projects],
          page: current_page,
          totalPages: total_pages,
          didInitialLoad: true,
          isFetching: false,
        });
      }).catch(console.error)
  }

  onTableProjectActionClick(key: string, project: Project) {
    switch (key) {
      case 'edit': this.onTableProjectEditClick(project)
        break
      case 'delete': this.onTableProjectDeleteClick(project)
        break
      default:
        throw Error('[Projects] Unimplemented onTableProjectActionClick')
    }
  }

  onTableSelectionChange(selectedProjectIds: string[]) {
    this.setState({ selectedProjectIds: selectedProjectIds })
  }

  onTableRowSelectionChange(selected: boolean, transactionId: string) {
    const { selectedProjectIds } = this.state

    if (selected) {
      this.setState({
        selectedProjectIds: [
          ...selectedProjectIds,
          transactionId
        ]
      })
    } else {
      this.setState({
        selectedProjectIds: selectedProjectIds.filter(selectedProjectId => selectedProjectId !== transactionId)
      })
    }
  }

  onTableProjectEditClick(project) {
    const { showProjectModal } = this.props

    showProjectModal({
      project: { id: project.id },
      onSubmit: this.onProjectUpdateFormSubmit
    })
  }

  onTableProjectDeleteClick(project) {
    const { showConfirmModal, refreshTimeline, t } = this.props

    showConfirmModal({
      title: t('ProjectsTable::Delete project'),
      description: t('ProjectsTable::You are about to delete project <b>{{name}}</b>. Deleting a project also deletes all its associated data. Are you sure?', { name: project.name }),
      action: { label: t('ProjectsTable::Delete'), isDestructive: true },
      onConfirm: () => {
        ProjectsController
          .delete(project.id)
          .then(response => {
            const { projects, page } = this.state

            Notification.notifySuccess(t('ProjectsTable::Project deleted successfully'))

            const projectIndex = projects.findIndex(c => c.id === project.id);

            projects.splice(projectIndex, 1);

            this.setState({
              projects: [...projects]
            })

            if (refreshTimeline) refreshTimeline()
          })
          .catch(console.error)
      }
    })
  }

  onNewProjectClick() {
    const { showProjectModal, status, contactId } = this.props

    showProjectModal({
      project: {
        // If project status not 
        status: status ? status : ProjectStatus.ACTIVE,
        contact_id: contactId
      },
      statusDisabled: Boolean(status),
      contactDisabled: Boolean(contactId),
      onSubmit: this.onProjectFormSubmit
    })
  }

  onTableProjectClick(project: Project) {
    this.props.history.push(RouteHelper.process(ERoute.PATH_PROJECT, { id: project.id }))
  }

  onProjectFiltersChange(activeFilters: any) {
    this.setState({ page: 1, activeFilters: activeFilters }, () => {
      this.rehydratedFetch()
    })
  }

  onProjectSortValueChange(value: string) {
    LocalStorage.set(LocalStorageKey.PROJECT_SORT_VALUE, value)

    this.setState({ page: 1, sortValue: value, }, () => {
      this.rehydratedFetch()
    })
  }

  onProjectSearchChange(search) {
    this.setState({ search: search })
  }

  onProjectSearchSubmit(search) {
    this.setState({ search: search }, () => {
      this.rehydratedFetch()
    })
  }

  onColumnsChange(columns: string[]) {
    LocalStorage.set(LocalStorageKey.PROJECT_COLUMNS, columns)
    this.forceUpdate()
  }

  onProjectClearFilters() {
    this.setState({
      page: 1,
      search: '',
      activeFilters: {}
    }, this.rehydratedFetch)
  }

  onPageChange(page: number) {
    this.setState({ page: page }, () => {
      this.rehydratedFetch()
    })
  }

  onPerPageChange(perPage: number) {
    this.setState({ perPage: perPage }, () => {
      this.rehydratedFetch()
    })
  }

  onActiveColumnsChange(columns: string[]) {
    LocalStorage.set(LocalStorageKey.PROJECT_COLUMNS, columns)
  }

  onProjectFormSubmit(project: Project) {
    const { refreshTimeline } = this.props
    const { projects } = this.state

    if (!this.props.status || project.status === this.props.status) {
      this.setState({
        projects: [
          project,
          ...projects,
        ],
      })
    }

    if (refreshTimeline) refreshTimeline()
  }

  onProjectUpdateFormSubmit(project: Project) {
    const { status, refreshTimeline } = this.props
    const { projects } = this.state

    if (project.status === status) {
      const projectIndex = projects.findIndex(p => p.id === project.id);

      if (projectIndex !== -1) {
        projects[projectIndex] = project
        projects[projectIndex]
      }

      this.setState({
        projects: [
          ...projects,
        ]
      })
    } else {
      const projectIndex = projects.findIndex(c => c.id === project.id);

      projects.splice(projectIndex, 1);

      this.setState({
        projects: [...projects]
      })
    }

    if (refreshTimeline) refreshTimeline()
  }

  onProjectCreateEvent(e: CustomEvent) {
    const { detail: project } = e

    this.onProjectFormSubmit(project)
  }


  onEmptyInfoActionClick() {
    this.onNewProjectClick()
  }

  onResourceBulkUpdateStatusActionClick() {
    const { t, status } = this.props
    const { selectedProjectIds, projects } = this.state

    this.props.showBulkProjectStatusUpdateModal({
      excludeStatuses: status ? [status] : [],
      onSubmit: async (status) => {
        try {
          const response = await ProjectsController.statusUpdateAll(selectedProjectIds, status)

          if (response.errors) { }
          else {
            const newProjects = projects.filter(p => !selectedProjectIds.includes(p.id));

            this.setState({ projects: [...newProjects], selectedProjectIds: [] });

            Notification.notifySuccess(t('ProjectsTable::Project statuses succesfully updated'))
          }
        } catch (ex) {
          console.error(ex)
        }
      }
    })
  }

  onResourceBulkDeleteActionClick() {
    const { t } = this.props
    const { selectedProjectIds, projects } = this.state

    this.props.showConfirmModal({
      title: t('ProjectsTable::Bulk project deletion'),
      description: t('ProjectsTable::You are about to delete these projects. By deleting these projects you are also deleting all its associated data. Are you sure?'),
      action: { label: t('ProjectsTable::Delete'), isDestructive: true },
      onConfirm: async () => {
        try {
          const response = await ProjectsController.deleteAll(selectedProjectIds)
          if (response.errors) { }
          else {
            const newProjects = projects.filter(p => !selectedProjectIds.includes(p.id));

            this.setState({ projects: [...newProjects], selectedProjectIds: [] });

            Notification.notifySuccess(t('ProjectsTable::Projects successfully deleted'))
          }

        } catch (ex) {
          console.error(ex)
        }
      }
    })
  }

  onResourceBulkAssignTagsActionClick() {
    const { t } = this.props
    const { selectedProjectIds, page } = this.state

    this.props.showBulkProjectTagModal({
      action: 'add',
      projectIds: selectedProjectIds,
      onSubmit: () => {
        this.setState({ selectedProjectIds: [] })
        Notification.notifySuccess(t('ProjectsTable::Tags successfully assigned to projects'))
        this.fetchProjects(page)
      }
    })
  }

  onResourceBulkRemoveTagsActionClick() {
    const { t } = this.props
    const { selectedProjectIds, page } = this.state

    this.props.showBulkProjectTagModal({
      action: 'remove',
      projectIds: selectedProjectIds,
      onSubmit: () => {
        this.setState({ selectedProjectIds: [] })
        Notification.notifySuccess(t('ProjectsTable::Tags successfully removed from projects'))
        this.fetchProjects(page)
      }
    })
  }

  render() {
    const {
      t,
      status,
      currentUser,
      primaryActionEnabled
    } = this.props
    const { workspace: { setting } } = currentUser
    const {
      didInitialLoad,
      projects,
      page,
      perPage,
      totalPages,
      isFetching,
      activeFilters,
      sortValue,
      search,
      selectedProjectIds,
    } = this.state

    const filtersActive = search?.length > 0 || Object.keys(activeFilters).length > 0

    let emptyDescription = filtersActive ? t('ProjectsTable::No projects found') : t('ProjectsTable::No projects have been created yet')
    let emptyActionText = filtersActive ? t('ProjectsTable::Clear filters') : t('ProjectsTable::Add project')

    if (status === ProjectStatus.PROPOSAL) {
      emptyDescription = filtersActive ? t('ProjectsTable::No proposal projects found') : t('ProjectsTable::No proposal projects have been created yet')
      emptyActionText = filtersActive ? t('ProjectsTable::Clear filters') : t('ProjectsTable::Add potential project')
    } else if (status === ProjectStatus.ACTIVE) {
      emptyDescription = filtersActive ? t('ProjectsTable::No active projects found') : t('ProjectsTable::No active projects have been created yet')
      emptyActionText = filtersActive ? t('ProjectsTable::Clear filters') : t('ProjectsTable::Add active project')
    } else if (status === ProjectStatus.ON_HOLD) {
      emptyDescription = filtersActive ? t('ProjectsTable::No on hold projects found') : t('ProjectsTable::No on hold projects have been created yet')
      emptyActionText = filtersActive ? t('ProjectsTable::Clear filters') : t('ProjectsTable::Add on hold project')
    } else if (status === ProjectStatus.COMPLETED) {
      emptyDescription = filtersActive ? t('ProjectsTable::No completed projects found') : t('ProjectsTable::No completed projects have been created yet')
      emptyActionText = filtersActive ? t('ProjectsTable::Clear filters') : t('ProjectsTable::Add completed project')
    } else if (status === ProjectStatus.CANCELLED) {
      emptyDescription = filtersActive ? t('ProjectsTable::No cancelled projects found') : t('ProjectsTable::No cancelled projects have been created yet')
      emptyActionText = filtersActive ? t('ProjectsTable::Clear filters') : t('ProjectsTable::Add cancelled project')
    }

    const promotedBulkActions: ResourceTableAction[] = [
      { icon: 'heartbeat', content: t('ProjectsTable::Update status'), onAction: this.onResourceBulkUpdateStatusActionClick },
      { icon: 'tag', content: t('ProjectsTable::Add tags'), onAction: this.onResourceBulkAssignTagsActionClick },
      { icon: 'tag', content: t('ProjectsTable::Remove tags'), onAction: this.onResourceBulkRemoveTagsActionClick },
      { icon: 'trash-alt-solid', content: t('ProjectsTable::Delete'), onAction: this.onResourceBulkDeleteActionClick }
    ]

    const filters: ProjectFilter[] = [
      { name: 'name', label: t('Project::Name'), type: ResourceListFilterType.STRING },
      { name: 'code', label: t('Project::Code'), type: ResourceListFilterType.STRING },
      { name: 'tag_ids', label: t('Contact::Tags'), type: ResourceListFilterType.MULTI_RESOURCE, resourceType: 'project_tag' },
      { name: 'po_number', label: t('Project::PO number'), type: ResourceListFilterType.STRING, visible: setting.po_number_enabled },
      { name: 'contact_id', label: t('Project::Contact'), type: ResourceListFilterType.SINGLE_RESOURCE, resourceType: 'contact', isValidNewOption: () => false },
      { name: 'billable_type', label: t('Project::Billing'), type: ResourceListFilterType.SINGLE_OPTION, options: ProjectHelper.getProjectBillableTypeOptions() },
      { name: 'hourly_rate', label: t('Project::Hourly rate'), type: ResourceListFilterType.NUMBER },
      { name: 'day_rate', label: t('Project::Day rate'), type: ResourceListFilterType.NUMBER },
      { name: 'budget', label: t('Project::Budget'), type: ResourceListFilterType.NUMBER },
      { name: 'budget_type', label: t('Project::Budget type'), type: ResourceListFilterType.SINGLE_OPTION, options: ProjectHelper.getBudgetTypeOptions() },
      { name: 'total_costs', label: t('Project::Costs'), type: ResourceListFilterType.NUMBER },
      { name: 'created_at', label: t('Project::Created date'), type: ResourceListFilterType.DATE },
    ]

    const { headers, columns, selectedColumns, renderRow } = getProjectTableRenderer({
      setting: setting,
      onRowClick: this.onTableProjectClick,
      selectedItems: selectedProjectIds,
      onSelectionChange: this.onTableRowSelectionChange,
      onActionClick: this.onTableProjectActionClick,
    })

    return (
      <>
        <Helmet>
          <title>{this.getTitle()}</title>
        </Helmet>
        <NavigationDetection onRouteChange={this.onRouteChange} />
        {!didInitialLoad && <PageLoader />}
        {didInitialLoad && <ResourceTable
          data={projects}
          selectedItems={selectedProjectIds}
          onSelectionChange={this.onTableSelectionChange}
          promotedBulkActions={promotedBulkActions}
          actionsLeft={primaryActionEnabled ? [
            <ButtonPanel
              icon='plus'
              text={t('ProjectTable::New project')}
              onClick={this.onNewProjectClick}
            />
          ] : null}
          headers={headers}
          renderRow={renderRow}
          renderEmpty={<CardEmptyInfo
            icon={filtersActive ? 'search' : 'briefcase'}
            description={emptyDescription}
            descriptionActionText={emptyActionText}
            onDescriptionActionClick={filtersActive ? this.onProjectClearFilters : this.onEmptyInfoActionClick}
          />}
          filters={filters}
          activeFilters={activeFilters}
          onFiltersChange={this.onProjectFiltersChange}
          sortOptions={[
            { label: '-', value: '-' },
            { label: t('ProjectsTable::Name (A-Z)'), value: 'name_asc' },
            { label: t('ProjectsTable::Name (Z-A)'), value: 'name_desc' },
            { label: t('ProjectsTable::Start date ↑'), value: 'start_date_asc' },
            { label: t('ProjectsTable::Start date ↓'), value: 'start_date_desc' },
            { label: t('ProjectsTable::End date ↑'), value: 'end_date_asc' },
            { label: t('ProjectsTable::End date ↓'), value: 'end_date_desc' },
          ]}
          sortValue={sortValue}
          onSortChange={this.onProjectSortValueChange}
          pagination={{ page: page, perPage: perPage, pageCount: totalPages }}
          onPageChange={this.onPageChange}
          onPerPageChange={this.onPerPageChange}
          isLoading={isFetching}
          stickyHeader={true}
          searchValue={search}
          onSearchChange={this.onProjectSearchChange}
          onSearchSubmit={this.onProjectSearchSubmit}
          columns={columns}
          selectedColumns={selectedColumns}
          onColumnsChange={this.onColumnsChange}
          maxHeight='65vh'
        />}
      </>
    )
  }
}

const mapStateToProps = (state: AppState): IStateToProps => {
  const {
    authentication: {
      currentUser,
    }
  } = state

  return {
    currentUser: currentUser,
  }
}

const mapDispatchToProps = (dispatch: Dispatch<AnyAction>): IDispatchToProps => {
  return {
    showProjectModal: (options) => dispatch(showProjectModal(options)),
    showConfirmModal: (options) => dispatch(showConfirmModal(options)),
    showBulkProjectStatusUpdateModal: (options) => dispatch(showBulkProjectStatusUpdateModal(options)),
    showBulkProjectTagModal: (options) => dispatch(showBulkProjectTagModal(options))
  }
}

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(withTranslation()(ProjectTable)) as any) as any